import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Badge } from '../../components/shared';
import { formatVolume } from '../../utils/format';
import {
    FLOW_GROUPS,
    hasFlow,
    type FlowGroupId,
    type LatestFlow,
} from '../flow/groups';
import { formatNetCapital, formatWallets } from '../flow/summary';
import type { Outcome } from '../model/types';
import { LiveProbability } from './LiveProbability';
import { isClosed, probabilityOf, volumeOf } from './ranking';

export const FLOW_SLOT_WIDTH = 120;

export const OUTCOME_GRID = {
    withFlow: 'minmax(0, 1fr) 150px 246px 120px',
    withoutFlow: 'minmax(0, 1fr) 150px 120px',
};

interface OutcomeRowProps {
    outcome: Outcome;
    color: string | null;
    selected: boolean;
    clickable: boolean;
    totalVolume: number;
    showFlow: boolean;
    flow: ReadonlyMap<FlowGroupId, LatestFlow> | undefined;
    onToggle: () => void;
}

export function OutcomeRow({
    outcome,
    color,
    selected,
    clickable,
    totalVolume,
    showFlow,
    flow,
    onToggle,
}: OutcomeRowProps) {
    const theme = useTheme();
    const probability = probabilityOf(outcome);
    const volume = volumeOf(outcome);
    const share = totalVolume > 0 ? volume / totalVolume : 0;
    const closed = isClosed(outcome);
    const barColor = color ?? theme.palette.text.disabled;

    return (
        <Box
            role={clickable ? 'button' : undefined}
            aria-pressed={clickable ? selected : undefined}
            data-testid={`outcome-row-${outcome.id}`}
            onClick={clickable ? onToggle : undefined}
            sx={{
                display: 'grid',
                gridTemplateColumns: showFlow
                    ? OUTCOME_GRID.withFlow
                    : OUTCOME_GRID.withoutFlow,
                alignItems: 'center',
                columnGap: 2,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                cursor: clickable ? 'pointer' : 'default',
                opacity: closed ? 0.6 : 1,
                bgcolor: selected
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'transparent',
                '&:hover': clickable
                    ? {
                          bgcolor: selected
                              ? alpha(theme.palette.primary.main, 0.14)
                              : 'action.hover',
                      }
                    : undefined,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        flexShrink: 0,
                        bgcolor: color ?? 'transparent',
                        border: color ? 0 : 1,
                        borderColor: 'divider',
                    }}
                />
                {outcome.image && (
                    <Box
                        component="img"
                        src={outcome.image}
                        alt=""
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 0.5,
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                )}
                <Typography
                    title={outcome.question}
                    sx={{
                        fontSize: '13px',
                        fontWeight: selected ? 700 : 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {outcome.label}
                </Typography>
                {closed && (
                    <Badge color={theme.palette.text.disabled}>Closed</Badge>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        flex: 1,
                        height: 5,
                        borderRadius: '3px',
                        overflow: 'hidden',
                        bgcolor: alpha(theme.palette.text.primary, 0.08),
                    }}
                >
                    <Box
                        sx={{
                            width: `${(probability ?? 0) * 100}%`,
                            height: '100%',
                            bgcolor: barColor,
                        }}
                    />
                </Box>
                <LiveProbability
                    outcome={outcome}
                    sx={{
                        width: 48,
                        textAlign: 'right',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        borderRadius: '2px',
                    }}
                />
            </Box>

            {showFlow && (
                <Box
                    data-testid={`flow-${outcome.id}`}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${FLOW_GROUPS.length}, ${FLOW_SLOT_WIDTH}px)`,
                        columnGap: '6px',
                    }}
                >
                    {FLOW_GROUPS.map(group => {
                        const value = flow?.get(group.id);
                        if (!value || !hasFlow(value)) {
                            return <span key={group.id} />;
                        }
                        return (
                            <Box
                                key={group.id}
                                title={`${group.label}: ${formatNetCapital(value)}, ${formatWallets(value.wallets)}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 0.5,
                                    px: 0.75,
                                    py: '1px',
                                    borderRadius: '10px',
                                    whiteSpace: 'nowrap',
                                    bgcolor: alpha(group.color, 0.12),
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            bgcolor: group.color,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {formatNetCapital(value)}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        fontFamily: 'monospace',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {value.wallets}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Typography
                sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                }}
            >
                {formatVolume(volume)} ({(share * 100).toFixed(0)}%)
            </Typography>
        </Box>
    );
}
